'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { creatorDescriptionReadMoreButtonClassName, creatorDescriptionTextClassName } from '@/app/[locale]/(worlds)/[roomId]/components/CreatorDescriptionReadMore';
import { getTextLineClampHeight } from '@/lib/text-line-clamp';
import { cn } from '@/lib/utils';
const COLLAPSED_LINES = 3;
type MarkdownDescriptionReadMoreProps = {
  /** Server-rendered `MarkdownDescriptionContent`. */
  content: ReactNode;
};

/**
 * Collapsible preview of the rich-text description.
 *
 * @remarks
 * Unlike `ReadMoreText`, it renders the content once and collapses with a measured `max-height`
 * instead of `line-clamp`, which is unreliable on block content (headings, lists). Until the
 * first measurement it falls back to three body lines (`3lh`) so the full text never flashes.
 */
function MarkdownDescriptionReadMore(t0) {
  const $ = _c(23);
  const {
    content
  } = t0;
  const t = useTranslations();
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (contentRef.current) {
        setCollapsedHeight(getTextLineClampHeight(contentRef.current, COLLAPSED_LINES));
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const measure = t1;
  const debouncedMeasure = useDebounceCallback(measure, 150);
  let t2;
  if ($[1] !== debouncedMeasure) {
    t2 = {
      ref: contentRef as RefObject<HTMLElement>,
      onResize: () => debouncedMeasure()
    };
    $[1] = debouncedMeasure;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useResizeObserver(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      measure();
      document.fonts?.ready.then(measure);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== content) {
    t4 = [content];
    $[4] = content;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  const isCollapsible = collapsedHeight != null;
  const t5 = collapsedHeight === undefined && "max-h-[3lh]";
  let t6;
  if ($[6] !== t5) {
    t6 = cn(creatorDescriptionTextClassName, "overflow-hidden wrap-break-word", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== collapsedHeight || $[9] !== expanded || $[10] !== isCollapsible) {
    t7 = isCollapsible && !expanded ? {
      maxHeight: collapsedHeight
    } : undefined;
    $[8] = collapsedHeight;
    $[9] = expanded;
    $[10] = isCollapsible;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== content || $[13] !== t6 || $[14] !== t7) {
    t8 = <div ref={contentRef} className={t6} style={t7}>{content}</div>;
    $[12] = content;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== expanded || $[17] !== isCollapsible || $[18] !== t) {
    t9 = isCollapsible && <button type="button" aria-expanded={expanded} onClick={() => setExpanded(_temp)} className={cn("text-vive-primary-40 body1-omni-styles mt-2 cursor-pointer hover:underline", creatorDescriptionReadMoreButtonClassName)}>{expanded ? t("profile-page.about.read-less") : t("profile-page.about.read-more")}</button>;
    $[16] = expanded;
    $[17] = isCollapsible;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div>{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp(prev) {
  return !prev;
}
export default MarkdownDescriptionReadMore;